import { isEmpty, isNil } from 'lodash';

import type {
  AddressAutocompleteRequest,
  ExpressPrediction,
} from '@appTypes/precisely';

import { addressTypeIds } from '../../constants';
import isValueTruthy from '../../utils/isValueTruthy';
import type { Address, CassAddress } from './ProfileAddressCard.types';

export const getNewAddress = (typeId: number | string) => ({
  id: `id_${typeId}_${new Date().toISOString().split('T')[0]}`,
  isPrimary: undefined,
  typeId: String(typeId),
  countryId: undefined,
});

const getPrimaryAddress = (
  addresses: Address[] = [],
  typeId: number | string,
) => {
  const filteredAddressesByType = addresses.filter(
    address => String(address.typeId) == String(typeId),
  );

  if (!filteredAddressesByType.length) {
    return getNewAddress(typeId);
  }

  const primaryAddress = filteredAddressesByType.find(address =>
    isValueTruthy(address.isPrimary),
  );

  return primaryAddress ? primaryAddress : filteredAddressesByType[0];
};

export const filterAddresses = (
  addresses: Address[] = [],
): Array<Address | ReturnType<typeof getNewAddress>> => {
  const homeAddress = getPrimaryAddress(addresses, addressTypeIds.HOME);
  const businessAddress = getPrimaryAddress(addresses, addressTypeIds.BUSINESS);
  const vacationAddress = getPrimaryAddress(addresses, addressTypeIds.VACATION);

  return [homeAddress, businessAddress, vacationAddress];
};

export const getCurrentPrimaryAddressTypeId = (addresses: Address[]) => {
  const primaryAddress = addresses.find(address =>
    isValueTruthy(address.isPrimary),
  );

  return primaryAddress ? Number(primaryAddress.typeId) : addressTypeIds.HOME;
};

export const getZipInitialValue = (
  zip: Address['zipCode'],
  zipExt: Address['zipCodeEx'],
) => {
  if (!zip) {
    return undefined;
  }

  return `${zip}${zipExt ? `-${zipExt}` : ''}`;
};

export function mapPredictionToAddress(
  prediction: ExpressPrediction,
): CassAddress {
  const zip5 = prediction.address.postalCode;
  const zip4 = prediction.address.postalCodeExt;

  const fullZip = isNil(zip4) || isEmpty(zip4) ? zip5 : `${zip5}-${zip4}`;

  return {
    AddressLine1: [prediction.address.addressNumber, prediction.address.street]
      .filter(Boolean)
      .join(' '),
    City: prediction.address.city.longName,
    CityAccepted: prediction.address.city.longName,
    CityNotAccepted: '',
    State: prediction.address.admin1.shortName,
    County: '',
    PostalCode: fullZip,
    CountrySubdivisionCode: `${prediction.address.country.isoAlpha3Code}-${prediction.address.admin1.shortName}`,
    AddressKey: prediction.customFields?.PB_KEY ?? '',
    SuiteName: prediction.address.unit ?? '',
    SuiteCount: prediction.address.unit ? 1 : 0,
    SuiteList: prediction.address.unit ? [prediction.address.unit] : [],
    PlusFour: [zip4 ?? ''],
    MAK: '',
    BaseMAK: '',
  };
}

export const autocompletePreferences: AddressAutocompleteRequest['preferences'] =
  {
    maxResults: 5,
    returnAllInfo: false,
    returnOfAdditionalFields: ['ZIP4'],
  };
